import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { UserProfile } from "@clerk/tanstack-react-start";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo, useRef } from "react";

import { SettingsPage } from "../../../components/dashboard/settings";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  createBillingCheckoutSession,
  createBillingInvoicePayment,
  createBillingPortalSession,
} from "@/features/settings/server-fns";
import {
  settingsBillingInvoicesQueryOptions,
  settingsBillingPlansQueryOptions,
  settingsUsageOverviewQueryOptions,
} from "@/features/settings/queries";
import { createClerkAppearance } from "@/lib/clerk-appearance";
import { useAppearance } from "@/lib/hooks/use-appearance";
import { findPalette, paletteClerkColors } from "@/lib/palettes";
import { capturePostHogEvent } from "@/lib/posthog-client";

type SettingsTab = "profile" | "appearance" | "usage" | "billing";

function parseSettingsTab(value: unknown): SettingsTab {
  return value === "appearance" || value === "usage" || value === "billing" ? value : "profile";
}

export const Route = createFileRoute("/dashboard/settings/")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: parseSettingsTab(search.tab),
  }),
  head: () => ({
    meta: [{ title: "Settings - Bavimail" }],
  }),
  staleTime: 30_000,
  preloadStaleTime: 30_000,
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: async ({ context, deps }) => {
    if (deps.tab === "profile" || deps.tab === "appearance") {
      return;
    }

    if (deps.tab === "usage") {
      await context.queryClient.ensureQueryData(settingsUsageOverviewQueryOptions());
      return;
    }

    await Promise.all([
      context.queryClient.ensureQueryData(settingsUsageOverviewQueryOptions()),
      context.queryClient.ensureQueryData(settingsBillingInvoicesQueryOptions()),
      context.queryClient.ensureQueryData(settingsBillingPlansQueryOptions()),
    ]);
  },
  component: SettingsRoute,
});

function SettingsRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { tab } = Route.useSearch();
  const settingsAnimationStage = useOneTimePageAnimation("dashboard-settings-page");
  const prewarmedTabsRef = useRef(new Map<string, number>());

  function shouldPrewarm(key: string) {
    const now = Date.now();
    const previous = prewarmedTabsRef.current.get(key);
    if (previous && now - previous < 3_000) {
      return false;
    }

    prewarmedTabsRef.current.set(key, now);
    return true;
  }

  async function prewarmSettingsTab(nextTab: SettingsTab) {
    const key = `settings:${nextTab}`;
    if (!shouldPrewarm(key)) return;

    await router.preloadRoute({
      to: "/dashboard/settings",
      search: { tab: nextTab },
    });

    if (nextTab === "profile" || nextTab === "appearance") {
      return;
    }

    if (nextTab === "usage") {
      await queryClient.prefetchQuery(settingsUsageOverviewQueryOptions());
      return;
    }

    await Promise.all([
      queryClient.prefetchQuery(settingsUsageOverviewQueryOptions()),
      queryClient.prefetchQuery(settingsBillingInvoicesQueryOptions()),
      queryClient.prefetchQuery(settingsBillingPlansQueryOptions()),
    ]);
  }

  const sharedProps = {
    activeTab: tab,
    settingsAnimationStage,
    onTabChange: (nextTab: SettingsTab) => {
      void navigate({
        to: "/dashboard/settings",
        search: { tab: nextTab === "profile" ? "profile" : nextTab },
        replace: true,
      });
    },
    onTabIntent: (nextTab: SettingsTab) => {
      void prewarmSettingsTab(nextTab);
    },
    onCreateBillingCheckoutSession: async (
      planCode: string,
      billingInterval: "monthly" | "annual",
    ) => {
      capturePostHogEvent("plan_checkout_started", { plan: planCode, interval: billingInterval });
      return await createBillingCheckoutSession({ data: { planCode, billingInterval } });
    },
    onCreateBillingPortalSession: async () => await createBillingPortalSession(),
    onCreateBillingInvoicePayment: async (invoiceId: string) =>
      await createBillingInvoicePayment({ data: { invoiceId } }),
  };

  if (tab === "appearance") {
    return <SettingsPage {...sharedProps} />;
  }

  if (tab === "usage") {
    return <SettingsUsageView {...sharedProps} />;
  }

  if (tab === "billing") {
    return <SettingsBillingView {...sharedProps} />;
  }

  return <SettingsProfileView {...sharedProps} />;
}

function SettingsProfileView(
  props: Omit<
    Parameters<typeof SettingsPage>[0],
    | "initialDomains"
    | "initialAuthSession"
    | "initialAuthAccounts"
    | "usageOverview"
    | "billingInvoices"
    | "billingPlans"
  >,
) {
  // Clerk is themed with literal colors, so hand it the palette on screen. Classic
  // Light keeps its dark tokens: the inversion filter flips Clerk with everything else.
  const appearance = useAppearance();
  const palette = appearance?.active ?? findPalette(undefined, "dark");
  const clerkAppearance = useMemo(
    () =>
      createClerkAppearance(
        paletteClerkColors(palette),
        palette.inverted ? "dark" : palette.mode,
        "embedded",
      ),
    [palette],
  );

  return (
    <SettingsPage
      {...props}
      profileContent={<UserProfile routing="hash" appearance={clerkAppearance} />}
    />
  );
}

function SettingsUsageView(
  props: Omit<
    Parameters<typeof SettingsPage>[0],
    | "usageOverview"
    | "initialDomains"
    | "initialAuthSession"
    | "initialAuthAccounts"
    | "billingInvoices"
    | "billingPlans"
  >,
) {
  const { data: usageOverview } = useSuspenseQuery(settingsUsageOverviewQueryOptions());

  return <SettingsPage {...props} usageOverview={usageOverview} />;
}

function SettingsBillingView(
  props: Omit<
    Parameters<typeof SettingsPage>[0],
    | "usageOverview"
    | "billingInvoices"
    | "billingPlans"
    | "initialDomains"
    | "initialAuthSession"
    | "initialAuthAccounts"
  >,
) {
  const { data: usageOverview } = useSuspenseQuery(settingsUsageOverviewQueryOptions());
  const { data: billingInvoices } = useSuspenseQuery(settingsBillingInvoicesQueryOptions());
  const { data: billingPlans } = useSuspenseQuery(settingsBillingPlansQueryOptions());

  return (
    <SettingsPage
      {...props}
      usageOverview={usageOverview}
      billingInvoices={billingInvoices}
      billingPlans={billingPlans}
    />
  );
}
